import React, { useEffect, useState } from "react";

const ESC_KEY = 27;

export type PopoverPosition =
  | "top-center"
  | "top-left"
  | "top-right"
  | "left"
  | "right"
  | "bottom-center"
  | "bottom-left"
  | "bottom-right";

interface PopoverLayout {
  x: number;
  y: number;
  paddingLeft: number;
  paddingRight: number;
  paddingBottom: number;
  paddingTop: number;
}

interface Dimensions {
  height: number;
  width: number;
}

interface PopoverChildrenProps<T extends HTMLElement> {
  ref: React.MutableRefObject<T | null>;
}

interface PopoverProps<T extends HTMLElement> {
  autoFocus?: boolean;
  isOpen?: boolean;
  onRequestClose?: () => void;
  content?: React.ReactNode;
  position?: PopoverPosition;
  children: (props: PopoverChildrenProps<T>) => React.ReactNode;
}

export function Popover<T extends HTMLElement>(props: PopoverProps<T>) {
  const {
    autoFocus,
    isOpen,
    content,
    children,
    onRequestClose = () => {},
    position = "top-center",
  } = props;
  const [state, setState] = React.useState({
    visible: false,
    layout: initialLayout,
  });
  const openerRef = React.useRef<T | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const [popoverDimensions, setPopoverDimensions] = useState<Dimensions | null>(
    null
  );
  const [openerDimensions, setOpenerDimensions] = useState<Dimensions | null>(
    null
  );

  const handleGetDimensions = React.useCallback(
    (ref: React.MutableRefObject<HTMLElement | null>): Dimensions | null => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();

        return { height: rect.height, width: rect.width };
      }

      return null;
    },
    []
  );

  useEffect(() => {
    if (openerRef.current) {
      const dimensions = handleGetDimensions(openerRef);
      setOpenerDimensions(dimensions);
    }
  }, [openerRef, handleGetDimensions]);

  useEffect(() => {
    if (popoverRef.current) {
      const dimensions = handleGetDimensions(popoverRef);

      // TODO: Figure out what is happening with the reflow process
      // that causes dimensions to be zero
      if (dimensions?.height === 0 || dimensions?.width === 0) {
        setTimeout(() => {
          const dimensions = handleGetDimensions(popoverRef);
          setPopoverDimensions(dimensions);
        }, 1000);
      } else {
        setPopoverDimensions(dimensions);
      }
    }
  }, [popoverRef, handleGetDimensions]);

  const handleKeyDown = React.useCallback(
    (event) => {
      if (event.keyCode === ESC_KEY) {
        event.stopPropagation();
        onRequestClose();
      }
    },
    [onRequestClose]
  );

  const handleBlur = React.useCallback(
    (event: React.FocusEvent) => {
      if (
        // @ts-ignore: if an element was pressed within the popover
        !event.currentTarget?.contains(event.relatedTarget) &&
        // @ts-ignore: if the press was on element within the opener
        !openerRef.current?.contains(event.relatedTarget) &&
        // if the press was on the opener
        event.relatedTarget !== openerRef.current
      ) {
        onRequestClose();
      }
    },
    [onRequestClose]
  );

  React.useEffect(() => {
    if (isOpen && openerDimensions && popoverDimensions) {
      const layout = calculatePopoverLayout(
        openerDimensions,
        popoverDimensions,
        4,
        position
      );

      setState({ visible: true, layout });

      if (autoFocus) {
        popoverRef.current?.focus();
      }
    } else {
      setState({ visible: false, layout: initialLayout });
    }
  }, [isOpen, position, autoFocus, openerDimensions, popoverDimensions]);

  return (
    <>
      <div
        tabIndex={-1}
        onBlur={handleBlur}
        style={{
          display: state.visible ? "flex" : "none",
          position: "absolute",
          boxSizing: "border-box",
          top: state.layout.y,
          left: state.layout.x,
          paddingLeft: state.layout.paddingLeft,
          paddingRight: state.layout.paddingRight,
          paddingBottom: state.layout.paddingBottom,
          paddingTop: state.layout.paddingTop,
          outline: "none",
        }}
        onKeyDown={handleKeyDown}
      >
        {content}
      </div>
      {popoverDimensions === null && (
        <div
          style={{
            opacity: 0,
            pointerEvents: "none",
            visibility: "hidden",
            position: "absolute",
          }}
          ref={popoverRef}
        >
          {content}
        </div>
      )}
      {children({ ref: openerRef })}
    </>
  );
}

const initialLayout = {
  x: 0,
  y: 0,
  paddingLeft: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingTop: 0,
};

function calculatePopoverLayout(
  openerDimensions: Dimensions,
  popoverDimensions: Dimensions,
  padding: number,
  position: PopoverPosition
): PopoverLayout {
  switch (position) {
    case "top-center":
      return {
        y: -popoverDimensions.height - padding,
        x: openerDimensions.width / 2 - popoverDimensions.width / 2,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: padding,
        paddingTop: 0,
      };
    case "top-right":
      return {
        y: -popoverDimensions.height - padding,
        x: 0 - (popoverDimensions.width - openerDimensions.width),
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: padding,
        paddingTop: 0,
      };
    case "bottom-right":
      return {
        y: openerDimensions.height,
        x: 0 - (popoverDimensions.width - openerDimensions.width),
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingTop: padding,
      };
    case "bottom-left":
      return {
        y: openerDimensions.height,
        x: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingTop: padding,
      };
    default:
      return {
        y: -popoverDimensions.height,
        x: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingTop: 0,
      };
  }
}
